import React, { useState } from 'react';
import { Grid, Button, TextField } from '@mui/material';
import { createJobCode } from '../../../../core/API/guestConnect';
import styles from '../SchedulerSetting.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';

interface CreateRewardsProps {
	backButtonClick: () => void;
	onRewardCreated?: () => void;
}

const validationSchema = Yup.object({
	title: Yup.string()
		.trim()
		.required('Reward offered is required')
		.min(1, 'Reward offered cannot be empty'),
	description: Yup.string()
		.trim()
		.required('Reward summary is required')
		.min(1, 'Reward summary cannot be empty'),
});

const CreateRewards: React.FC<CreateRewardsProps> = ({ backButtonClick, onRewardCreated }) => {
	const [apiError, setApiError] = useState<string | null>(null);

	const formik = useFormik({
		initialValues: {
			title: '',
			description: '',
		},
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			setApiError(null);

			try {
				const getWorkplace = () => {
					const workplaceId = localStorage.getItem("workplace-id");
					return workplaceId ? `/api/workplaces/${workplaceId}` : null;
				};
				const workplace = getWorkplace();
				if (!workplace) {
					setApiError('Workplace ID is required');
					setSubmitting(false);
					return;
				}
				await createJobCode({
					title: values.title,
					description: values.description,
					workplace: workplace,
				});

				onRewardCreated?.();

				// Navigate back after a short delay
				setTimeout(() => {
					backButtonClick();
				}, 1500);
			} catch (err: any) {
				setApiError(err?.message || 'Failed to create reward');
				setSubmitting(false);
			}
		},
	});

	return (
		<div className={styles.createJobCode}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={backButtonClick}>
						Back
					</Button>
					<Button 
						className={styles.submitButton} 
						onClick={() => formik.handleSubmit()} 
						disabled={formik.isSubmitting}
					>
						{formik.isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Create Guest Connect Reward</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{apiError && (
					<div style={{ color: '#d32f2f', marginBottom: 16 }}>{apiError}</div>
				)}
				
				<TextField
					label="Reward Offered"
					name="title"
					placeholder="Enter title"
					value={formik.values.title}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.title && Boolean(formik.errors.title)}
					helperText={formik.touched.title && formik.errors.title}
				/>

				<TextField
					label="Reward Summary to Display on Campaign"
					name="description"
					type="text"
					value={formik.values.description}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					placeholder="Enter text"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					multiline
					rows={4}
					error={formik.touched.description && Boolean(formik.errors.description)}
					helperText={formik.touched.description && formik.errors.description}
				/>

			</form>
		</div>
	);
};

export default CreateRewards;
